import { RiPrinterFill } from '@remixicon/react'
import React, { useRef, useState } from 'react'
import QRCode from 'react-qr-code'
import { useReactToPrint } from 'react-to-print'

import { Button } from '../ui/button'
import { NumberInput } from '../ui/number-input'

import { Card } from '@/components/ui/card'
import { usePrintStickers } from '@/store/print-stickers'

interface SkidStickersProps {
  skidId: number
  skidLabel: string
  skidType: 'SKI' | 'LHB' | 'MOS' | 'OUT'
}

/**
 * Encode the QR as a packaging URL with `?skid=<label>` so that scanning it via the AHK
 * browser-helper navigates to the corresponding skid sheet. The two-way scan flow
 * (bundle ↔ skid) parses this URL on the receiving side to extract the label.
 */
function buildSkidStickerLink(skidLabel: string): string {
  return `${window.location.origin}/packaging?skid=${encodeURIComponent(skidLabel)}&ship-date=all`
}

const PrintableContent = React.forwardRef<HTMLDivElement, SkidStickersProps & { count: number }>(
  ({ skidLabel, count }, ref) => {
    const qrValue = buildSkidStickerLink(skidLabel)
    return (
      <div ref={ref}>
        {Array.from({ length: count }).map((_, index) => (
          <React.Fragment key={index}>
            <Card
              className='mx-auto gap-2.5 rounded-md border p-5 font-medium text-[#000]'
              style={{ width: '6in', height: '4in', boxSizing: 'border-box' }}
            >
              <div className='flex size-full items-start gap-3'>
                <div className='flex size-full flex-col'>
                  <img
                    className='-mt-2 -ml-3.5 w-52 grayscale'
                    src='/maco-50-logo.svg'
                    alt='Maco enterprises inc'
                  />
                  <div className='space-y-2'>
                    <div className='text-base leading-tight'>www.macoenterprises.ca</div>
                    <div className='text-base leading-none'>519-638-2746</div>
                  </div>
                  <div className='mt-auto'>
                    <div className='font-mono text-5xl leading-none tracking-tight'>{skidLabel}</div>
                  </div>
                </div>
                <div className='flex h-full flex-col items-end justify-end'>
                  <div className='size-25'>
                    <QRCode
                      className='size-full rounded-sm'
                      value={qrValue}
                    />
                  </div>
                </div>
              </div>
            </Card>
            {index < count - 1 ? <div className='page-break-after' /> : null}
          </React.Fragment>
        ))}
      </div>
    )
  }
)

PrintableContent.displayName = 'SkidStickersPrintableContent'

export const SkidStickers = ({ skidId, skidLabel, skidType }: SkidStickersProps) => {
  const [count, setCount] = useState(1)
  const contentRef = useRef<HTMLDivElement>(null)
  const { setPrintSkid } = usePrintStickers()

  const reactToPrintFn = useReactToPrint({
    contentRef,
    onBeforePrint: async () => {
      setTimeout(() => setPrintSkid(null), 200)
    },
    pageStyle: `
      @page { size: 6in 4in; margin: 0; }
      @media print {
        html, body { margin: 0; -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
        .page-break-after { page-break-after: always; break-after: page; height: 0 !important; overflow: hidden !important; }
        /* Match the bundle sticker QR footprint — handheld scanners fit this in frame. */
        .size-25 { width: 1.05in !important; height: 1.05in !important; }
      }
    `,
    suppressErrors: process.env.NODE_ENV !== 'production'
  })

  const qrPreview = buildSkidStickerLink(skidLabel)

  return (
    <div className='w-full'>
      <div className='no-print mx-auto w-2/3'>
        <Card
          className='mx-auto gap-2.5 rounded-md border p-5 font-medium text-[#000]'
          style={{ width: '6in', height: '4in', boxSizing: 'border-box' }}
        >
          <div className='flex size-full items-start gap-3'>
            <div className='flex size-full flex-col'>
              <img
                className='-mt-2 -ml-3.5 w-52 grayscale'
                src='/maco-50-logo.svg'
                alt='Maco enterprises inc'
              />
              <div className='space-y-2'>
                <div className='text-base leading-tight'>www.macoenterprises.ca</div>
                <div className='text-base leading-none'>519-638-2746</div>
              </div>
              <div className='mt-auto'>
                <div className='font-mono text-5xl leading-none tracking-tight'>{skidLabel}</div>
              </div>
            </div>
            <div className='flex h-full flex-col items-end justify-end'>
              <div className='size-25'>
                <QRCode
                  className='size-full rounded-sm'
                  value={qrPreview}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div style={{ display: 'none' }}>
        <PrintableContent
          ref={contentRef}
          skidId={skidId}
          skidLabel={skidLabel}
          skidType={skidType}
          count={count}
        />
      </div>

      <div className='mt-4 flex w-full items-end gap-3.5'>
        <div className='flex flex-1 flex-col gap-0.5'>
          <div className='text-base text-[#000]'>Stickers to print</div>
          <NumberInput
            className='[&_input]:bg-white-100 [&_button]:bg-white-100 [&_button]:hover:!bg-white-100'
            onChange={setCount}
            value={count}
            minValue={1}
          />
        </div>
        <Button
          className='flex-1'
          onClick={reactToPrintFn}
        >
          <RiPrinterFill />
          <span>Print {count} sticker(s)</span>
        </Button>
      </div>
    </div>
  )
}
